<template>
  <div>
    <!-- SECTION HEADLINE -->
    <div id="sectionTitle">
      <b-container>
        <b-row>
          <b-col></b-col>
          <b-col sm="12" md="10">
            <div class="sectionHeadline">
              <h1>Experiences &amp; Certifications</h1>
              <p>
                Hands-on experiences and certified skills that shaped my path
              </p>
            </div>
          </b-col>
          <b-col></b-col>
        </b-row>
      </b-container>
    </div>
    <!-- WORK -->
    <content-section theme="dark">
      <b-row>
        <b-col class="text-center">
          <h1>Work Experience</h1>
          <p>Time to turn what I’ve learned into something that matters</p>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <!-- WORK 1 -->
          <info-card theme="dark">
            <h2 slot="title">Lead / Senior Mobile Engineer (Maybank)</h2>
            <p slot="tagline">
              Malaysia’s largest bank, and my greatest responsibility yet
              <b>(Jul 2025 - Present)</b>
            </p>
            <b-carousel slot="custom-carousel" controls indicators>
              <b-carousel-slide
                v-for="(item, i) in maybankLeadSeniorWorkExperienceCarousel"
                :key="i"
                :img-src="item.src"
              >
              </b-carousel-slide>
            </b-carousel>
            <div slot="description">
              <p>
                As a Lead / Senior Mobile Engineer at Maybank, I spearheaded the
                design, development, and delivery of secure, high-performing
                mobile banking experiences that serve millions of users daily.
                My role combined technical leadership, cross-functional
                collaboration, and a strong focus on app performance,
                reliability, and security.
              </p>
              <ul>
                <li>
                  Led the technical design and delivery of the MAE mobile
                  platform, used daily by over 10 million Maybank customers,
                  implementing secure features including Digital Identity,
                  MFA/OTP, and Payments (DuitNow, FPX, Interbank Transfers)
                </li>
                <li>
                  Optimized app performance and reliability by reducing crash
                  rates and enhancing overall security through interim
                  safeguards and integration with external application security
                  providers
                </li>
                <li>
                  Represented mobile engineering in architecture/compliance
                  reviews to influence platform decisions
                </li>
                <li>
                  Collaborated with cross-functional squads, including Security,
                  Product, DevOps, Backend, QA & Compliance teams to deliver
                  releases under BNM and PCI regulatory requirements
                </li>
                <li>
                  Led engineering excellence by mentoring mobile engineers,
                  enforcing coding standards, and setting interview/assessment
                  processes to hire top talent
                </li>
                <li>
                  Coordinated production deployment, incident resolution, and
                  root cause analysis for critical services
                </li>
                <li>
                  Delivered secure, reliable, and scalable mobile banking
                  solutions, contributing to Maybank’s mission of providing
                  trusted digital experiences
                </li>
              </ul>
            </div>
          </info-card>
        </b-col>
      </b-row>

      <b-row>
        <b-col>
          <!-- WORK 2 -->
          <info-card theme="dark">
            <h2 slot="title">Mobile Engineer L2 (MoneyLion)</h2>
            <p slot="tagline">
              New year, new challenge. A new chapter in my career journey
              <b>(Jan 2025 - Jun 2025)</b>
            </p>
            <b-carousel slot="custom-carousel" controls indicators>
              <b-carousel-slide
                v-for="(item, i) in moneyLionL2WorkExperienceCarousel"
                :key="i"
                :img-src="item.src"
              >
              </b-carousel-slide>
            </b-carousel>
            <div slot="description">
              <p>
                As a Mobile Engineer L2 in a fast-paced Fintech environment, I
                design and develop secure, high-performance mobile applications
                that enable seamless financial experiences for users. Working
                primarily with React Native, I collaborate closely with product,
                design, and backend teams to build features that prioritize
                usability, security, and scalability.
              </p>
              <ul>
                <li>
                  Developed and maintained cross-platform mobile applications
                  (iOS/Android) using React Native and web features using React
                  within a 15-member cross-functional Agile team (5M+ downloads,
                  4.7 star app rating)
                </li>
                <li>
                  Integrated financial APIs in mobile apps to enable real-time
                  data sync, secure transactions, analytics, localization, and
                  A/B testing using Split, Segment, Embrace, Datadog, and
                  Crowdin
                </li>
                <li>
                  Collaborated with designers and product managers to implement
                  intuitive UI/UX with a mobile-first approach
                </li>
                <li>
                  Participated performance optimization efforts, reducing app
                  load times and improving responsiveness
                </li>
                <li>
                  Developed Jest unit tests using a BDD approach and Shift Left
                  methodology to enhance code reliability
                </li>
                <li>
                  Participated in code reviews, knowledge sharing, and mentoring
                  of other engineers
                </li>
              </ul>
            </div>
          </info-card>
        </b-col>
      </b-row>

      <b-row>
        <b-col>
          <!-- WORK 3 -->
          <info-card theme="dark">
            <h2 slot="title">
              [Promotion] Senior Mobile Engineer (Hitachi Digital Host)
            </h2>
            <p slot="tagline">
              Through persistence in challenging times, promoted to Senior
              Mobile Engineer
              <b>(Apr 2023 - Dec 2024)</b>
            </p>
            <b-carousel slot="custom-carousel" controls indicators>
              <b-carousel-slide
                v-for="(item, i) in hdhSeniorWorkExperienceCarousel"
                :key="i"
                :img-src="item.src"
              >
              </b-carousel-slide>
            </b-carousel>
            <div slot="description">
              <p>
                Participated in award-winning mobile development that drove
                record-breaking revenue — delivering high-quality, scalable
                features with React Native and TypeScript.
              </p>
              <ul>
                <li>
                  Received the “Team Excellence Award 2023” for leading
                  front-end contributions to a high-impact project, delivering
                  top-tier product quality and the highest operating profit in
                  the 2022 fiscal year
                </li>
                <li>
                  Assumed end-to-end ownership of multiple front-end modules,
                  driving the planning, design, refactoring, and implementation
                  of scalable solutions extensively using React, TypeScript &
                  Jest
                </li>
                <li>
                  Provided technical mentorship to junior developers, promoting
                  efficient development practices and helping them achieve
                  deliverables through effective guidance
                </li>
                <li>
                  Collaborated with Senior Engineers and Technical Leads to
                  define coding standards, review implementations, and drive
                  continuous improvement in development practices
                </li>
                <li>
                  Delivered production-ready code with >95% unit test coverage,
                  maintaining on-schedule delivery while ensuring system
                  reliability and performance
                </li>
              </ul>
            </div>
          </info-card>
        </b-col>
      </b-row>

      <b-row>
        <b-col>
          <!-- WORK 4 -->
          <info-card theme="dark">
            <h2 slot="title">Mobile Engineer (Hitachi Digital Host)</h2>
            <p slot="tagline">
              The saga continues in Kuala Lumpur, Malaysia as a Mobile Engineer
              <b>(Feb 2021 - Apr 2023)</b>
            </p>
            <b-carousel slot="custom-carousel" controls indicators>
              <b-carousel-slide
                v-for="(item, i) in hdhJuniorWorkExperienceCarousel"
                :key="i"
                :img-src="item.src"
              >
              </b-carousel-slide>
            </b-carousel>
            <div slot="description">
              <p>
                Built and scaled a secure, cross-platform fintech app using
                React Native, React, and TypeScript — integrating analytics,
                crash reporting, and real-time transactions.
              </p>
              <ul>
                <li>
                  Developed the front-end of a financial services application
                  using React Native for mobile and React for web, with
                  extensive use of TypeScript across both platforms
                </li>
                <li>
                  Integrated third-party solutions for enhanced functionality,
                  including transaction security, push notifications (Airship),
                  and app analytics using Dynatrace and Firebase Crashlytics
                </li>
                <li>
                  Built reusable UI components and utility functions to improve
                  code maintainability and consistency across the project
                </li>
                <li>
                  Conducted bug triage to identify root causes and implement
                  effective solutions
                </li>
                <li>
                  Collaborated closely with back-end teams to ensure smooth
                  feature and logic integration across the system
                </li>
                <li>
                  Implemented unit testing with Jest to validate UI components
                  and business logic, ensuring stability and reliability
                </li>
              </ul>
            </div>
          </info-card>
        </b-col>
      </b-row>
    </content-section>

    <!-- INTERNSHIP -->
    <content-section theme="dark">
      <b-row>
        <b-col class="text-center">
          <h1>Internship</h1>
          <p>A brief but meaningful intro to the world of tech and teamwork</p>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <!-- INTERNSHIP 1 -->
          <info-card theme="dark">
            <h2 slot="title">Application Engineer (Hokenso)</h2>
            <p slot="tagline">
              Kuala Lumpur, Malaysia and Singapore as an Application Engineer
              Intern <b>(Jul 2019 - Oct 2019)</b>
            </p>
            <b-carousel slot="custom-carousel" controls indicators>
              <b-carousel-slide
                v-for="(item, i) in hokensoInternshipCarousel"
                :key="i"
                :img-src="item.src"
              >
              </b-carousel-slide>
            </b-carousel>
            <div slot="description">
              <p>
                Drove seamless system integration and front-end development
                across multi-site deployments — combining clean UI
                implementation with deep technical troubleshooting and team
                enablement.
              </p>
              <ul>
                <li>
                  Supported System Integration Testing (SIT) across Malaysia and
                  Singapore sites to ensure seamless deployment
                </li>
                <li>
                  Developed responsive user interfaces and integrated business
                  logic based on high-fidelity design specifications
                </li>
                <li>
                  Performed in-depth debugging and testing to resolve issues
                  related to UI/UX, logic flow, and back-end functionality
                </li>
                <li>
                  Recommended and evaluated suitable frameworks to enhance
                  project efficiency and meet technical objectives
                </li>
                <li>
                  Conducted technical knowledge transfer sessions for new team
                  members, explaining system architecture, logic, and component
                  communication
                </li>
              </ul>
              <p></p>
            </div>
          </info-card>
        </b-col>
      </b-row>
    </content-section>

    <!-- COMPETITION PARTICIPATION -->
    <content-section theme="dark">
      <b-row>
        <b-col class="text-center">
          <h1>Competition Participation</h1>
          <p>
            A collection of hackathons and coding challenges I’ve participated
            in throughout my journey as a developer
          </p>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <!-- COMPETITION 1 -->
          <info-card theme="dark">
            <h2 slot="title">CIMB 3D Conquest (2018)</h2>
            <p slot="tagline">
              How we won customer confidence with Biometric Verification
            </p>
            <b-carousel slot="custom-carousel" controls indicators>
              <b-carousel-slide
                v-for="(item, i) in cimbConquestCarousel"
                :key="i"
                :img-src="item.src"
              >
              </b-carousel-slide>
            </b-carousel>
            <div slot="description">
              <p>
                I took part in this competition as part of a team. To get in, we
                had to go through several assessment stages. Once selected, we
                were given a challenge to solve a problem related to mobile
                banking security. Our solution? An Android app called
                ProLog—designed to secure the login process using biometric
                authentication.
              </p>
            </div>
            <router-link
              :to="{ name: 'softwareProjects', hash: '#prolog-project' }"
              slot="button"
              tag="b-button"
              class="button-dark big-button"
              >Go to ProLog project
            </router-link>
          </info-card>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <!-- COMPETITION 2 -->
          <info-card theme="dark">
            <h2 slot="title">NASA Space Apps Challenge (2018)</h2>
            <p slot="tagline">
              Multiple natural disasters, one smart solution for each
            </p>
            <b-carousel slot="custom-carousel" controls indicators>
              <b-carousel-slide
                v-for="(item, i) in nasaSpaceAppsCarousel"
                :key="i"
                :img-src="item.src"
              >
              </b-carousel-slide>
            </b-carousel>
            <div slot="description">
              <p>
                In response to the 2018 tsunami in Indonesia, we set out to
                create a website that helps people stay informed and prepared
                for natural disaster emergencies. The result was Disaster Escape
                Plan—a platform designed to educate users and guide them through
                specific action plans tailored to different types of disasters.
              </p>
            </div>
            <router-link
              :to="{
                name: 'softwareProjects',
                hash: '#disaster-escape-plan-project',
              }"
              slot="button"
              tag="b-button"
              class="button-dark big-button"
              >Go to Disaster Escape Plan project
            </router-link>
          </info-card>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <!-- COMPETITION 3 -->
          <info-card theme="dark">
            <h2 slot="title">MMU 3 Days of Code Hackathon (2018)</h2>
            <p slot="tagline">
              Everyone learns differently, and that’s why teaching should never
              be one-size-fits-all
            </p>
            <b-carousel slot="custom-carousel" controls indicators>
              <b-carousel-slide
                v-for="(item, i) in mmuHackathonCarousel"
                :key="i"
                :img-src="item.src"
              >
              </b-carousel-slide>
            </b-carousel>
            <div slot="description">
              <p>
                My first team-based hackathon! We were challenged to come up
                with a solution to improve the education sector and help
                students have a better learning experience. Our team built
                TutorMe—an online tutoring platform that connects students with
                tutors based on each student’s unique learning style.
              </p>
            </div>
            <router-link
              :to="{ name: 'softwareProjects', hash: '#tutor-me-project' }"
              slot="button"
              tag="b-button"
              class="button-dark big-button"
              >Go to TutorMe project
            </router-link>
          </info-card>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <!-- COMPETITION 4 -->
          <info-card theme="dark">
            <h2 slot="title">E-Genting Programming Competition (2017, 2018)</h2>
            <p slot="tagline">Pushing my coding abilities to the edge</p>
            <b-carousel slot="custom-carousel" controls indicators>
              <b-carousel-slide
                v-for="(item, i) in eGentingCarousel"
                :key="i"
                :img-src="item.src"
              >
              </b-carousel-slide>
            </b-carousel>
            <div slot="description">
              <p>
                This programming competition is held annually, challenging
                participants to solve five tough problems using accurate and
                efficient algorithms. I join every year as a way to test and
                push my coding skills.
              </p>
            </div>
          </info-card>
        </b-col>
      </b-row>
    </content-section>
    <!-- CERTIFICATIONS -->
    <content-section theme="light">
      <b-row>
        <b-col class="text-center">
          <h1>Certifications</h1>
          <p>
            Verified credentials in Agile Project Management and Software
            Engineering
          </p>
        </b-col>
      </b-row>
      <!-- Agile Project Management -->
      <b-row>
        <b-col>
          <showcase-card theme="light">
            <h2 slot="title">Agile Project Management</h2>
            <b-container slot="showcase-content">
              <b-row>
                <b-col
                  sm="12"
                  lg="6"
                  v-for="(item, i) in agileProjectManagementCarousel"
                  :key="i"
                >
                  <b-img :src="item.src" fluid></b-img>
                </b-col>
              </b-row>
            </b-container>
          </showcase-card>
        </b-col>
      </b-row>
      <!-- CCA -->
      <b-row>
        <b-col>
          <showcase-card theme="light">
            <h2 slot="title">Cambridge Certification Authority (CCA)</h2>
            <b-container slot="showcase-content">
              <b-row>
                <b-col
                  sm="12"
                  lg="6"
                  v-for="(item, i) in ccaCertificationCarousel"
                  :key="i"
                >
                  <b-img :src="item.src" fluid></b-img>
                </b-col>
              </b-row>
            </b-container>
          </showcase-card>
        </b-col>
      </b-row>
      <!-- Dicoding -->
      <b-row>
        <b-col>
          <showcase-card theme="light">
            <h2 slot="title">Dicoding</h2>
            <b-container slot="showcase-content">
              <b-row>
                <b-col
                  sm="12"
                  lg="6"
                  v-for="(item, i) in dicodingCertificationCarousel"
                  :key="i"
                >
                  <b-img :src="item.src" fluid></b-img>
                </b-col>
              </b-row>
            </b-container>
          </showcase-card>
        </b-col>
      </b-row>
      <!-- Iverson -->
      <b-row>
        <b-col>
          <showcase-card theme="light">
            <h2 slot="title">Iverson</h2>
            <b-container slot="showcase-content">
              <b-row>
                <b-col
                  sm="12"
                  lg="6"
                  v-for="(item, i) in iversonCertificationCarousel"
                  :key="i"
                >
                  <b-img :src="item.src" fluid></b-img>
                </b-col>
              </b-row>
            </b-container>
          </showcase-card>
        </b-col>
      </b-row>
      <!-- Google -->
      <b-row>
        <b-col>
          <showcase-card theme="light">
            <h2 slot="title">Google</h2>
            <b-container slot="showcase-content">
              <b-row>
                <b-col
                  sm="12"
                  lg="6"
                  v-for="(item, i) in googleCertificationCarousel"
                  :key="i"
                >
                  <b-img :src="item.src" fluid></b-img>
                </b-col>
              </b-row>
            </b-container>
          </showcase-card>
        </b-col>
      </b-row>
    </content-section>
    <!-- ADDITIONAL LEARNING -->
    <content-section theme="light">
      <b-row>
        <b-col class="text-center">
          <h1>Additional Learning</h1>
          <p>
            Extra knowledge gained through online courses, workshops, and
            self-study
          </p>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <!-- INSERT INFORMATION CARD HERE -->
          <showcase-card>
            <h2 slot="title">Udemy &amp; Pluralsight</h2>
            <b-container slot="showcase-content">
              <b-row>
                <b-col
                  sm="12"
                  lg="6"
                  v-for="(item, i) in additionalLearningCarousel"
                  :key="i"
                >
                  <b-img :src="item.src" fluid></b-img>
                </b-col>
              </b-row>
            </b-container>
          </showcase-card>
        </b-col>
      </b-row>
    </content-section>
    <!-- FOOTER -->
    <footer-section theme="dark"></footer-section>
  </div>
</template>

<script>
import InformationCard from "@/components/InformationCard";
import ShowcaseCard from "@/components/ShowcaseCard";
import ContentSection from "../components/ContentSection.vue";

export default {
  name: "Achievements",
  components: {
    "info-card": InformationCard,
    "showcase-card": ShowcaseCard,
    "content-section": ContentSection,
  },
  data() {
    return {
      maybankLeadSeniorWorkExperienceCarousel: [
        {
          src: require("@/assets/common/images/work/maybank-lead-senior-1.jpg"),
        },
        {
          src: require("@/assets/common/images/work/maybank-lead-senior-2.jpg"),
        },
      ],
      moneyLionL2WorkExperienceCarousel: [
        {
          src: require("@/assets/common/images/work/moneylion-L2-1.jpg"),
        },
      ],
      hdhSeniorWorkExperienceCarousel: [
        {
          src: require("@/assets/common/images/work/hitachi-digital-host-3.jpg"),
        },
        {
          src: require("@/assets/common/images/work/hitachi-digital-host-4.jpeg"),
        },
      ],
      hdhJuniorWorkExperienceCarousel: [
        {
          src: require("@/assets/common/images/work/hitachi-digital-host-1.png"),
        },
        {
          src: require("@/assets/common/images/work/hitachi-digital-host-2.png"),
        },
      ],
      hokensoInternshipCarousel: [
        {
          src: require("@/assets/common/images/internship/hokenso-internship-1.jpg"),
        },
        {
          src: require("@/assets/common/images/internship/hokenso-internship-2.jpg"),
        },
      ],
      cimbConquestCarousel: [
        {
          src: require("@/assets/common/images/certificate/cimb-3d-conquest.png"),
        },
        {
          src: require("@/assets/common/images/competition/competition-cimb-1-2018.png"),
        },
        {
          src: require("@/assets/common/images/competition/competition-cimb-2-2018.png"),
        },
      ],
      nasaSpaceAppsCarousel: [
        {
          src: require("@/assets/common/images/certificate/nasa-space-apps.png"),
        },
        {
          src: require("@/assets/common/images/competition/competition-nasa-2018.png"),
        },
      ],
      mmuHackathonCarousel: [
        {
          src: require("@/assets/common/images/certificate/mmu-hackathon.png"),
        },
        {
          src: require("@/assets/common/images/competition/competition-mmu-2018.png"),
        },
      ],
      eGentingCarousel: [
        {
          src: require("@/assets/common/images/certificate/e-genting-2018.png"),
        },
        {
          src: require("@/assets/common/images/competition/competition-egenting-1-2018.png"),
        },
        {
          src: require("@/assets/common/images/competition/competition-egenting-2-2018.png"),
        },
      ],
      agileProjectManagementCarousel: [
        {
          src: require("@/assets/common/images/certificate/scrum-psm1.png"),
        },
        {
          src: require("@/assets/common/images/certificate/icagile-icp.png"),
        },
      ],
      ccaCertificationCarousel: [
        {
          src: require("@/assets/common/images/certificate/cca-html-level-1.png"),
        },
        {
          src: require("@/assets/common/images/certificate/cca-css-level-1.png"),
        },
        {
          src: require("@/assets/common/images/certificate/cca-javascript-level-1.png"),
        },
        {
          src: require("@/assets/common/images/certificate/cca-jquery-level-1.png"),
        },
        {
          src: require("@/assets/common/images/certificate/cca-mysql-level-1.png"),
        },
        {
          src: require("@/assets/common/images/certificate/cca-php-level-1.png"),
        },
        {
          src: require("@/assets/common/images/certificate/cca-java-level-1.png"),
        },
        {
          src: require("@/assets/common/images/certificate/cca-java-level-2.png"),
        },
      ],
      dicodingCertificationCarousel: [
        {
          src: require("@/assets/common/images/certificate/dicoding-0.png"),
        },
        {
          src: require("@/assets/common/images/certificate/dicoding-1.png"),
        },
        {
          src: require("@/assets/common/images/certificate/dicoding-2.png"),
        },
      ],
      iversonCertificationCarousel: [
        {
          src: require("@/assets/common/images/certificate/iverson-reactjs.png"),
        },
        {
          src: require("@/assets/common/images/certificate/iverson-react-native.png"),
        },
        {
          src: require("@/assets/common/images/certificate/iverson-react-with-typescript.png"),
        },
      ],
      googleCertificationCarousel: [
        {
          src: require("@/assets/common/images/certificate/google-fundamentals-of-digital-marketing.png"),
        },
      ],
      additionalLearningCarousel: [
        {
          src: require("@/assets/common/images/certificate/the-ultimate-guide-to-game-development-with-unity.png"),
        },
        {
          src: require("@/assets/common/images/certificate/pluralsight-practical-html5.png"),
        },
      ],
    };
  },
  methods: {
    openLink(link) {
      window.open(link);
    },
  },
};
</script>

<style scoped>
div#sectionTitle {
  height: 500px;
  line-height: 460px;
  background: linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)),
    url("./../assets/common/images/certificate/certificate-headline.png");
  filter: grayscale(1);
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  text-align: center;
  color: white;
  padding-bottom: 0;
  padding-top: 0;
}

div.sectionHeadline {
  display: inline-block;
  vertical-align: middle;
  line-height: normal;
}

ul {
  list-style-position: inside;
}
</style>
